/**
 * Intestazione aziendale usata nei documenti generati (preventivi, ordini, DDT).
 *
 * I valori arrivano dal profilo azienda via API e vengono depositati qui dallo
 * store all'avvio. Prima venivano da `VUE_APP_COMPANY_*`, quindi erano fissati
 * al momento della build: cambiare l'intestazione richiedeva una ricompilazione
 * per cliente.
 *
 * È un registro e non un modulo dello store perché i generatori PDF sono
 * funzioni pure, senza accesso al contesto Vue.
 */

const EMPTY = Object.freeze({
  name: "",
  subtitle: "",
  addressLine: "",
  city: "",
  contact: "",
  vatNumber: "",
  iban: "",
});

let current = { ...EMPTY };
let logo = null; // { dataUrl, format } — pronto per jsPDF.addImage

/** Formato immagine richiesto da jsPDF, dedotto dal data URL. */
function formatOf(dataUrl) {
  const match = /^data:image\/([a-z]+);/i.exec(String(dataUrl || ""));
  if (!match) return null;
  const type = match[1].toLowerCase();
  if (type === "jpeg" || type === "jpg") return "JPEG";
  if (type === "png") return "PNG";
  if (type === "webp") return "WEBP";
  return null;
}

/** Deposita il profilo letto dall'API. Un valore assente diventa stringa vuota. */
export function setCompanyProfile(profile) {
  const clean = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    const value = profile?.[key];
    clean[key] = value === null || value === undefined ? "" : String(value);
  }
  current = clean;
  return current;
}

/** Deposita il logo già convertito in data URL. `null` lo rimuove. */
export function setCompanyLogo(dataUrl) {
  const format = formatOf(dataUrl);
  logo = format ? { dataUrl, format } : null;
  return logo;
}

/** Riporta il registro allo stato iniziale (logout, o test). */
export function resetCompanyProfile() {
  current = { ...EMPTY };
  logo = null;
}

/** Logo pronto per `doc.addImage`, oppure `null` se non ce n'è uno. */
export function companyLogo() {
  return logo;
}

export const companyProfile = {
  get name() {
    return current.name;
  },
  get iban() {
    return current.iban;
  },
  get vatNumber() {
    return current.vatNumber;
  },
  /** Righe della testata sotto il nome. Le voci vuote vengono ignorate. */
  get headerLines() {
    return [
      current.subtitle,
      current.addressLine,
      current.city,
      current.contact,
      current.vatNumber ? `P. IVA ${current.vatNumber}` : "",
    ].filter(Boolean);
  },
};

/**
 * Testata completa: nome più righe descrittive.
 *
 * Se il profilo non è ancora stato configurato l'elenco è vuoto e il documento
 * esce senza intestazione: meglio di un'intestazione altrui.
 */
export function companyHeaderLines() {
  return [companyProfile.name, ...companyProfile.headerLines].filter(Boolean);
}

export default companyProfile;
